import { useState } from "react";
import { useDispatch } from "react-redux";
import { trackEvent } from "src/Amplitude";
import {
  setBuyAssetSide,
  setSellAssetSide,
  useTargetStateV2,
} from "src/store/features/TargetOrdersV2.slice";

interface DispatchArgs {
  setTargetTradeAmount?: (value: number | string) => void;
  resetFormField?: (field: string) => void;
  fieldIdentifier?: string;
}

export const useTargetInputV2 = ({
  setTargetTradeAmount,
  resetFormField,
  fieldIdentifier,
}: DispatchArgs = {}) => {
  const dispatch = useDispatch();

  const [enteredValue, setEnteredValue] = useState<number | string>();
  const [isTouched, setIsTouched] = useState(false);
  const [reseted, setRested] = useState<string | undefined>("");

  const { sellAssetSide, buyAssetSide } = useTargetStateV2();

  const zeroInput = (enteredValue && +enteredValue > 0) || false;

  const hasError = !zeroInput && isTouched;
  const valueValid: boolean = +enteredValue! > 0 && !hasError;

  const handleTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value) {
      dispatch(setSellAssetSide({ ...sellAssetSide, value, units: value }));
      trackEvent("User Changed Sell Input", { sellAssetSide }, "Target");
    }
  };

  const handleCurrencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value) {
      dispatch(setBuyAssetSide({ ...buyAssetSide, value, units: value }));
      trackEvent("User Changed Currency Input", { buyAssetSide }, "Target");
    }
  };

  const handleSetMaxUnits = (maxTotal: string) => {
    dispatch(setSellAssetSide({ ...sellAssetSide, units: maxTotal }));
  };

  const valueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseFloat(event.target.value?.replace(/,/g, ""));

    if (value || value === 0) {
      setEnteredValue(value);
      if (setTargetTradeAmount) {
        // Conditional check
        setIsTouched(true);
        dispatch(setTargetTradeAmount(value));
      }
    } else {
      setEnteredValue("");
      if (resetFormField && fieldIdentifier && setTargetTradeAmount) {
        // Conditional check
        dispatch(setTargetTradeAmount(""));
        dispatch(resetFormField(fieldIdentifier)); // Resetting the field
      }
    }
  };

  const inputBlurHandler = () => {
    setIsTouched(true);
    setRested("");
  };

  const reset = () => {
    setEnteredValue("");
    setIsTouched(false);
    setRested(undefined);
    if (setTargetTradeAmount) {
      dispatch(setTargetTradeAmount(""));
    }
  };

  return {
    reset,
    reseted,
    value: enteredValue,
    hasError,
    isTouched,
    isValid: valueValid,
    inputBlurHandler,
    zeroInput,
    valueChangeHandler,
    handleTokenChange,
    handleCurrencyChange,
    handleSetMaxUnits,
  };
};
